import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import { useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { setAltText, setAltTextEnabled } from 'services/compose'
import { composerAltTextEnabledSelector, composerAltTextSelector } from 'services/compose/selectors'
import { ALT_TEXT_MAX_LENGTH } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import styles from './AltText.pcss'

export function AltText({ hint }: { hint?: React.ReactNode }) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const enabled = useSelector(composerAltTextEnabledSelector)
  const text = useSelector(composerAltTextSelector)
  const hintId = useId()
  const helperId = useId()

  const onToggle = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    dispatch(setAltTextEnabled(checked))
  }

  // NOTE: no maxLength - the 500 char limit is soft by design. Going over surfaces an error here and
  // disables the composer actions for as long as the control is on screen (see composerAltTextErrorSelector).
  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setAltText(e.target.value))
  }

  const isOverLimit = text.length > ALT_TEXT_MAX_LENGTH

  return (
    <div className={styles.container} data-testid="composer-alt-text">
      <FormControlLabel
        label={<FormattedMessage id="composer.labels.alt-text-toggle" />}
        control={(
          <Switch
            size="small"
            checked={enabled}
            color="primary"
            classes={{ root: styles['switch-root'] }}
            // EXPL: MUI renders the input as a plain checkbox - role="switch" matches what it looks like
            slotProps={{ input: { role: 'switch' } }}
            onChange={onToggle}
          />
        )}
        className={styles.toggle}
        classes={{ label: styles['toggle-label'] }}
      />
      {hint && <div id={hintId}>{hint}</div>}
      {enabled && (
        <>
          <TextField
            value={text}
            multiline
            minRows={2}
            error={isOverLimit}
            className={styles.box}
            slotProps={{
              input: { classes: { input: styles.input } },
              htmlInput: {
                'aria-label': intl.formatMessage({ id: 'composer.labels.alt-text-input' }),
                'aria-describedby': hint ? `${hintId} ${helperId}` : helperId
              }
            }}
            placeholder={intl.formatMessage({ id: 'composer.labels.alt-text-placeholder' })}
            name="Alt text"
            onChange={onTextChange}
          />
          <div className={styles.footer}>
            {/* EXPL: live region stays mounted (empty under the limit) - one that mounts already filled is often not announced */}
            <span id={helperId} aria-live="polite" className={`${styles.helper} ${isOverLimit ? styles.error : ''}`}>
              {isOverLimit && <FormattedMessage id="composer.errors.alt-text-too-long" />}
            </span>
            <span className={`${styles.counter} ${isOverLimit ? styles.error : ''}`} data-testid="composer-alt-text-counter">
              {`${text.length}/${ALT_TEXT_MAX_LENGTH}`}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

export default AltText
